import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { authAPI } from '../../api/authAPI';
import { itemsAPI } from '../../api/itemsAPI';
import { getMarketplaceItemDetailPath } from '../../utils/itemLinks';
import { formatListingPriceLabel } from '../../utils/formatPrice';
import OpenInFleaBanner from '../../components/items/OpenInFleaBanner';

const Container = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 1.5rem 1rem 3rem;
`;

const ProfileCard = styled.section`
  background: var(--bg-primary, #fff);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: var(--shadow-sm, 0 1px 2px rgba(15, 23, 42, 0.06));
`;

const ProfileTop = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const Avatar = styled.div`
  width: 112px;
  height: 112px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: linear-gradient(145deg, #0f766e 0%, #14b8a6 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  font-weight: 700;
  border: 3px solid #ecfdf5;
  box-shadow: 0 0 0 1px #d1fae5;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 640px) {
    width: 96px;
    height: 96px;
    font-size: 2.1rem;
  }
`;

const ProfileMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;

  @media (max-width: 640px) {
    width: 100%;
    align-items: center;
  }
`;

const IdentityBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const DisplayName = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary, #0f172a);
  line-height: 1.25;
  word-break: break-word;
`;

const Handle = styled.p`
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
  color: var(--primary-color, #0f766e);
`;

const MetaLine = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: var(--text-secondary, #64748b);
`;

const ActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;

  @media (max-width: 640px) {
    justify-content: center;
    width: 100%;
  }
`;

const FollowButton = styled.button`
  padding: 0.55rem 1.25rem;
  min-height: 44px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  border: 1.5px solid ${(p) => (p.$following ? 'var(--border-color, #cbd5e1)' : 'transparent')};
  color: ${(p) => (p.$following ? 'var(--text-primary, #0f172a)' : '#fff')};
  background: ${(p) => (p.$following ? '#fff' : 'var(--primary-color, #0f766e)')};

  &:hover {
    filter: brightness(0.97);
  }
`;

const SocialStatsRow = styled.div`
  display: flex;
  gap: 1.75rem;
  padding-top: 0.25rem;

  @media (max-width: 640px) {
    justify-content: center;
    gap: 2rem;
  }
`;

const SocialStat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => (p.$center ? 'center' : 'flex-start')};
`;

const SocialStatButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  color: inherit;

  &:hover span:last-child {
    color: var(--primary-color, #0f766e);
  }

  @media (max-width: 640px) {
    align-items: center;
  }
`;

const SocialStatNumber = styled.span`
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--text-primary, #0f172a);
`;

const SocialStatLabel = styled.span`
  font-size: 0.8rem;
  color: var(--text-secondary, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const Tabs = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
`;

const Tab = styled.button`
  padding: 0.7rem 1rem;
  border: none;
  background: none;
  font-weight: 600;
  color: ${(p) => (p.$active ? 'var(--primary-color, #0f766e)' : 'var(--text-secondary, #64748b)')};
  border-bottom: 2px solid ${(p) => (p.$active ? 'var(--primary-color, #0f766e)' : 'transparent')};
  margin-bottom: -1px;
  cursor: pointer;

  &:hover {
    color: var(--primary-color, #0f766e);
  }
`;

const SectionTitle = styled.h2`
  margin: 0 0 1rem;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-primary, #0f172a);
`;

const EmptyState = styled.p`
  margin: 0;
  padding: 2rem 1rem;
  text-align: center;
  color: var(--text-secondary, #64748b);
  background: var(--bg-secondary, #f8fafc);
  border-radius: 0.75rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
`;

const Card = styled(Link)`
  text-decoration: none;
  color: inherit;
  background: var(--bg-primary, #fff);
  border-radius: 0.85rem;
  border: 1px solid var(--border-color, #e2e8f0);
  overflow: hidden;
  position: relative;
  opacity: ${(p) => (p.$soldOut ? 0.7 : 1)};
  transition: box-shadow 0.15s ease, transform 0.15s ease;

  &:hover {
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
    transform: translateY(-1px);
  }
`;

const CardImage = styled.div`
  aspect-ratio: 1;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 1.75rem;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
    transform: scale(1.1);
  }
`;

const CardBody = styled.div`
  padding: 0.75rem 0.85rem 0.95rem;
`;

const CardTitle = styled.h3`
  margin: 0 0 0.25rem;
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.3;
  color: var(--text-primary, #0f172a);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardMeta = styled.p`
  margin: 0 0 0.35rem;
  font-size: 0.78rem;
  color: var(--text-secondary, #64748b);
`;

const CardPrice = styled.strong`
  font-size: 0.95rem;
  color: var(--primary-color, #0f766e);
`;

const TypeChip = styled.span`
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  z-index: 1;
  padding: 0.2rem 0.45rem;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #fff;
  background: ${(p) => p.$bg || '#0f766e'};
`;

const SoldOutChip = styled.span`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: rgba(15, 23, 42, 0.8);
  color: #fff;
  padding: 0.25rem 0.5rem;
  border-radius: 0.35rem;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const FollowingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.85rem;
`;

const FollowingCard = styled(Link)`
  text-decoration: none;
  color: inherit;
  background: var(--bg-primary, #fff);
  border-radius: 0.85rem;
  border: 1px solid var(--border-color, #e2e8f0);
  padding: 0.85rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.07);
  }
`;

const MiniAvatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: linear-gradient(145deg, #0f766e 0%, #14b8a6 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const FollowingInfo = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
`;

const FollowingUsername = styled.strong`
  font-size: 0.95rem;
  color: var(--text-primary, #0f172a);
`;

const FollowingName = styled.span`
  font-size: 0.85rem;
  color: var(--text-secondary, #64748b);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const resolveItemImage = (item) => {
  if (!item) return null;
  if (item.primary_image) return item.primary_image;
  if (item.image_url) return item.image_url;
  if (item.images?.length) {
    const first = item.images[0];
    return typeof first === 'string' ? first : first?.image || first?.url || null;
  }
  return null;
};

const resolveCatalogueType = (item) => {
  if (item?.is_luxury === true || item?.is_luxury === 1 || item?.is_luxury === 'true') {
    return { label: 'Luxury', bg: '#7c3aed' };
  }
  return { label: 'Pre-owned', bg: '#0f766e' };
};

const PublicProfilePage = () => {
  const { username } = useParams();
  const { user: currentUser } = useSelector((state) => state.auth);
  const [profile, setProfile] = useState(null);
  const [items, setItems] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('catalogue');
  const [followingLoading, setFollowingLoading] = useState(false);

  const load = useCallback(async () => {
    if (!username) return;
    setLoading(true);
    try {
      const profileData = await authAPI.getPublicProfile(username);
      setProfile(profileData);
      const ownerItems = await itemsAPI.getPublicUserItems(profileData.id);
      setItems(ownerItems || []);
    } finally {
      setLoading(false);
    }
  }, [username]);

  useEffect(() => {
    load();
  }, [load]);

  const loadFollowing = useCallback(async () => {
    if (!profile?.username) return;
    setFollowingLoading(true);
    try {
      const list = await authAPI.getFollowing(profile.username);
      setFollowing(Array.isArray(list) ? list : []);
    } catch {
      setFollowing([]);
    } finally {
      setFollowingLoading(false);
    }
  }, [profile?.username]);

  useEffect(() => {
    if (tab === 'following') loadFollowing();
  }, [tab, loadFollowing]);

  const toggleFollow = async () => {
    if (!profile?.username) return;
    if (profile.is_following) {
      await authAPI.unfollowProfile(profile.username);
    } else {
      await authAPI.followProfile(profile.username);
    }
    await load();
  };

  if (loading) return <Container>Loading profile...</Container>;
  if (!profile) return <Container>Profile not found.</Container>;

  const isOwnProfile =
    currentUser?.username &&
    profile.username &&
    currentUser.username.toLowerCase() === profile.username.toLowerCase();

  const displayName = profile.name?.trim() || profile.username;
  const avatarInitial = (displayName || '?').charAt(0).toUpperCase();
  const listingCount = items.length;

  return (
    <Container>
      <ProfileCard>
        <ProfileTop>
          <Avatar aria-hidden={!profile.profile_picture}>
            {profile.profile_picture ? (
              <img src={profile.profile_picture} alt="" />
            ) : (
              avatarInitial
            )}
          </Avatar>

          <ProfileMain>
            <IdentityBlock>
              <DisplayName>{displayName}</DisplayName>
              <Handle>@{profile.username}</Handle>
              {listingCount > 0 ? (
                <MetaLine>
                  {listingCount} {listingCount === 1 ? 'listing' : 'listings'} in catalogue
                </MetaLine>
              ) : (
                <MetaLine>Seller on Flea</MetaLine>
              )}
            </IdentityBlock>

            <SocialStatsRow>
              <SocialStat $center>
                <SocialStatNumber>{profile.followers_count ?? 0}</SocialStatNumber>
                <SocialStatLabel>Followers</SocialStatLabel>
              </SocialStat>
              {isOwnProfile ? (
                <SocialStatButton type="button" onClick={() => setTab('following')}>
                  <SocialStatNumber>{profile.following_count ?? 0}</SocialStatNumber>
                  <SocialStatLabel>Following</SocialStatLabel>
                </SocialStatButton>
              ) : (
                <SocialStat $center>
                  <SocialStatNumber>{profile.following_count ?? 0}</SocialStatNumber>
                  <SocialStatLabel>Following</SocialStatLabel>
                </SocialStat>
              )}
              <SocialStat $center>
                <SocialStatNumber>{listingCount}</SocialStatNumber>
                <SocialStatLabel>Items</SocialStatLabel>
              </SocialStat>
            </SocialStatsRow>

            <ActionsRow>
              {!isOwnProfile ? (
                <>
                  <FollowButton type="button" onClick={toggleFollow} $following={profile.is_following}>
                    {profile.is_following ? 'Following' : 'Follow'}
                  </FollowButton>
                  <FollowButton
                    type="button"
                    $following
                    style={{ borderColor: '#ef4444', color: '#ef4444' }}
                    onClick={async () => {
                      try {
                        if (profile.is_blocked) {
                          await authAPI.unblockProfile(profile.username);
                          setProfile((prev) => ({ ...prev, is_blocked: false }));
                        } else {
                          await authAPI.blockProfile(profile.username, 'Abusive user');
                          setProfile((prev) => ({ ...prev, is_blocked: true, is_following: false }));
                          setItems([]);
                          alert(
                            'User blocked. You will no longer see their content. We have been notified and will review within 24 hours.',
                          );
                        }
                      } catch (err) {
                        alert(err?.response?.data?.error || 'Could not update block status');
                      }
                    }}
                  >
                    {profile.is_blocked ? 'Unblock user' : 'Block user'}
                  </FollowButton>
                </>
              ) : (
                <FollowButton as={Link} to="/profile" $following>
                  Edit profile
                </FollowButton>
              )}
            </ActionsRow>
          </ProfileMain>
        </ProfileTop>
      </ProfileCard>
      {!isOwnProfile ? (
        <OpenInFleaBanner
          path={`/u/${encodeURIComponent(profile.username)}`}
          titleKey="itemDetail.downloadFleaToFollow"
        />
      ) : null}

      <Tabs>
        <Tab type="button" $active={tab === 'catalogue'} onClick={() => setTab('catalogue')}>
          Catalogue
        </Tab>
        {isOwnProfile ? (
          <Tab type="button" $active={tab === 'following'} onClick={() => setTab('following')}>
            Following ({profile.following_count ?? 0})
          </Tab>
        ) : null}
      </Tabs>

      {tab === 'catalogue' && (
        <>
          <SectionTitle>Public catalogue</SectionTitle>
          {items.length === 0 ? (
            <EmptyState>No public items yet.</EmptyState>
          ) : (
            <Grid>
              {items.map((item) => {
                const soldOut = item.in_stock != null && Number(item.in_stock) === 0;
                const imageUrl = resolveItemImage(item);
                const typeMeta = resolveCatalogueType(item);
                return (
                  <Card
                    key={item.id}
                    to={getMarketplaceItemDetailPath(item)}
                    state={{ itemId: item.id }}
                    $soldOut={soldOut}
                  >
                    <TypeChip $bg={typeMeta.bg}>{typeMeta.label}</TypeChip>
                    {soldOut && <SoldOutChip>Sold out</SoldOutChip>}
                    <CardImage>
                      {imageUrl ? <img src={imageUrl} alt="" loading="lazy" /> : '📦'}
                    </CardImage>
                    <CardBody>
                      <CardTitle>{item.title}</CardTitle>
                      {item.category ? <CardMeta>{item.category}</CardMeta> : null}
                      <CardPrice>{formatListingPriceLabel(item)}</CardPrice>
                    </CardBody>
                  </Card>
                );
              })}
            </Grid>
          )}
        </>
      )}

      {isOwnProfile && tab === 'following' && (
        <>
          <SectionTitle>Following</SectionTitle>
          {followingLoading ? (
            <EmptyState>Loading...</EmptyState>
          ) : following.length === 0 ? (
            <EmptyState>Not following anyone yet.</EmptyState>
          ) : (
            <FollowingGrid>
              {following.map((u) => {
                const name = u.name?.trim() || u.username;
                const initial = (name || '?').charAt(0).toUpperCase();
                return (
                  <FollowingCard key={u.id} to={`/u/${u.username}`}>
                    <MiniAvatar>
                      {u.profile_picture ? (
                        <img src={u.profile_picture} alt="" />
                      ) : (
                        initial
                      )}
                    </MiniAvatar>
                    <FollowingInfo>
                      <FollowingUsername>@{u.username}</FollowingUsername>
                      <FollowingName>{name}</FollowingName>
                    </FollowingInfo>
                  </FollowingCard>
                );
              })}
            </FollowingGrid>
          )}
        </>
      )}
    </Container>
  );
};

export default PublicProfilePage;
